/**
 * @component ry-tag
 * @category  display
 * @summary   Inline pill / tag label (Fomantic UI `.ui.label` equivalent). Pure display by default; opt into `[removable]` to render a close button that removes the tag from the DOM and emits `ry:remove`. Carry a value through `data-value` so the remove event identifies which tag was dismissed.
 *
 * Reference: Fomantic UI's label element. ry-tag focuses on the inline-chip
 * pattern (filter chips, selected-value pills, taxonomy markers) and the
 * removable case. For richer label variants (pointing, attached, ribbon,
 * corner) use the theme's `variant` / `size` CSS hooks.
 *
 * @attribute variant     string?              Visual variant — typically maps to the palette (`primary`, `success`, `warning`, `danger`, `info`). Pure CSS hook.
 * @attribute size        'xs' | 'sm' | 'lg' ? Pure CSS hook for compact / standard / enlarged tag sizes.
 * @attribute removable   boolean   default=false   Renders a close button after the label that calls `remove()` and fires `ry:remove`.
 * @attribute data-value  string?              Application identifier carried through to the `ry:remove` event detail. Use for "which tag was removed" without parsing the label text.
 *
 * @property  label       string    Read-only — the trimmed text content of the label slot.
 *
 * @event ry:beforeremove
 *        detail: { value: string, label: string }
 *        fires:  when the close button is clicked (mouse, or Enter/Space on the button) — before `ry:remove` and before the tag detaches
 *        cancelable: preventDefault() keeps the tag in the DOM; `ry:remove` does not fire
 *
 * @event ry:remove
 *        detail: { value: string, label: string }
 *        fires:  when the close button is clicked. `detail.value` is the tag's `data-value`; `detail.label` is the label text. The tag removes itself from DOM in the same task — listeners can save the value before re-mount logic runs.
 *
 * @content   The tag's textContent is the label. The original text is lifted into `[data-ry-target="label"]` at setup; the close button is appended after when `[removable]`. After setup, query the label via `el.label` or `[data-ry-target="label"]`.
 *
 * @dom
 *   ry-tag[variant?][size?][removable?][data-value?]
 *     > span.ry-tag__label    [data-ry-target="label"]
 *         > {label text}
 *     > button.ry-tag__remove [data-ry-target="remove"] type="button" aria-label="Remove"    (only when [removable])
 *         > {close icon}
 *
 * @gotcha  Clicking the remove button calls `this.remove()` — the element is detached from DOM immediately. If you have listeners on a parent that need to inspect the tag after click, do it inside the `ry:remove` handler before the synchronous `remove()` runs (this happens *after* emit, so the tag is still attached when listeners run).
 *
 * @gotcha  The remove button calls `e.stopPropagation()` so the click does NOT bubble. Wrapping `<ry-tag removable>` inside an interactive parent (e.g. `<ry-card interactive>`) won't see the close click — by design, the close action is local.
 *
 * @gotcha  `removable` is read once at setup. Adding the attribute later does NOT add a close button — and removing it does NOT remove the rendered button. To toggle removability at runtime, re-render or replace the element.
 *
 * @gotcha  `data-value` is purely consumer-controlled. ry-tag does not validate or normalize it — pass whatever identifier your data layer needs.
 *
 * @example basic
 * ```html
 * <ry-tag>Default</ry-tag>
 * ```
 *
 * @example variants
 * ```html
 * <ry-tag variant="primary">Primary</ry-tag>
 * <ry-tag variant="success">Success</ry-tag>
 * <ry-tag variant="warning">Warning</ry-tag>
 * <ry-tag variant="danger">Danger</ry-tag>
 * ```
 *
 * @example removable
 * ```html
 * <ry-tag removable data-value="js">JavaScript</ry-tag>
 * <ry-tag removable data-value="ts">TypeScript</ry-tag>
 * ```
 *
 * @example sizes
 * ```html
 * <ry-tag size="xs">Extra small</ry-tag>
 * <ry-tag size="sm">Small</ry-tag>
 * <ry-tag>Default</ry-tag>
 * <ry-tag size="lg">Large</ry-tag>
 * ```
 *
 * @example filter-list
 * ```html
 * <div>
 *   <ry-tag removable data-value="frontend">Frontend</ry-tag>
 *   <ry-tag removable data-value="backend">Backend</ry-tag>
 *   <ry-tag removable data-value="design">Design</ry-tag>
 * </div>
 * ```
 *
 * @tutorial mount-lifts-text-into-label
 * ```js
 * const el = mount('<ry-tag>JavaScript</ry-tag>');
 * const label = el.querySelector('[data-ry-target="label"]');
 * await expectEqual(label.textContent.trim(), 'JavaScript', 'label slot holds the text');
 * await expectEqual(el.label, 'JavaScript', '.label property reflects label slot');
 * ```
 *
 * @tutorial no-remove-button-by-default
 * ```js
 * // Wrong assumption: every tag has a close button. The button is opt-in
 * // via [removable]; without it the tag is purely display.
 * const el = mount('<ry-tag>X</ry-tag>');
 * await expectEqual(el.querySelector('[data-ry-target="remove"]'), null, 'no remove button by default');
 * ```
 *
 * @tutorial removable-renders-close-button
 * ```js
 * const el = mount('<ry-tag removable>X</ry-tag>');
 * const btn = el.querySelector('[data-ry-target="remove"]');
 * await expectEqual(!!btn, true, 'close button rendered');
 * await expectAttr(btn, 'type', 'button', 'is a non-submitting button');
 * await expectAttr(btn, 'aria-label', 'Remove');
 * ```
 *
 * @tutorial remove-click-detaches-and-emits
 * ```js
 * // Wrong assumption: removable tags only emit, leaving the DOM alone. They
 * // also detach themselves. Listeners receive ry:remove just BEFORE remove().
 * const wrap = mount('<div><ry-tag removable data-value="js">JavaScript</ry-tag></div>');
 * const tag = wrap.querySelector('ry-tag');
 * const fired = [];
 * tag.addEventListener('ry:remove', (e) => fired.push({
 *   value: e.detail.value,
 *   label: e.detail.label,
 *   attached: tag.isConnected,
 * }));
 * tag.querySelector('[data-ry-target="remove"]').click();
 * await expectEqual(fired.length, 1, 'ry:remove fired');
 * await expectEqual(fired[0].value, 'js', 'detail.value reflects data-value');
 * await expectEqual(fired[0].label, 'JavaScript', 'detail.label reflects label text');
 * await expectEqual(fired[0].attached, true, 'still attached when listener runs');
 * await expectEqual(tag.isConnected, false, 'detached after click resolves');
 * ```
 *
 * @tutorial remove-click-stops-propagation
 * ```js
 * // Wrong assumption: clicks on the close button bubble to the parent.
 * // ry-tag stops propagation so wrapping click handlers stay quiet.
 * const wrap = mount('<div id="outer"><ry-tag removable>X</ry-tag></div>');
 * let outerClicks = 0;
 * wrap.addEventListener('click', () => outerClicks++);
 * wrap.querySelector('[data-ry-target="remove"]').click();
 * await expectEqual(outerClicks, 0, 'parent did not see the close click');
 * ```
 *
 * @tutorial data-value-defaults-to-empty
 * ```js
 * // Wrong assumption: missing data-value sends some auto-generated id.
 * // It sends the empty string — the label text is still in detail.label.
 * const wrap = mount('<div><ry-tag removable>NoValue</ry-tag></div>');
 * const tag = wrap.querySelector('ry-tag');
 * const fired = [];
 * tag.addEventListener('ry:remove', (e) => fired.push(e.detail));
 * tag.querySelector('[data-ry-target="remove"]').click();
 * await expectEqual(fired[0].value, '', 'detail.value is empty string when data-value is missing');
 * await expectEqual(fired[0].label, 'NoValue');
 * ```
 *
 * @tutorial beforeremove-veto
 * ```js
 * // preventDefault() on ry:beforeremove keeps the tag mounted and silences
 * // ry:remove. Without the veto the same click detaches it.
 * const wrap = mount('<div><ry-tag removable data-value="js">JavaScript</ry-tag></div>');
 * const tag = wrap.querySelector('ry-tag');
 * const removed = [];
 * tag.addEventListener('ry:remove', (e) => removed.push(e.detail));
 * tag.addEventListener('ry:beforeremove', (e) => e.preventDefault(), { once: true });
 * tag.querySelector('[data-ry-target="remove"]').click();
 * await expectEqual(tag.isConnected, true, 'vetoed remove leaves the tag attached');
 * await expectEqual(removed.length, 0, 'ry:remove did not fire');
 * tag.querySelector('[data-ry-target="remove"]').click();
 * await expectEqual(tag.isConnected, false, 'without the veto, the click removes the tag');
 * await expectEqual(removed[0].value, 'js', 'ry:remove carries the value');
 * ```
 *
 * @see ry-tag-input
 * @see ry-button
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

export class RyTag extends RyElement {
  setup(): void {
    this.#transformMarkup();
  }

  #transformMarkup(): void {
    if (this.$('[data-ry-target="label"]')) return;

    const text = this.textContent?.trim() ?? '';
    const removable = this.hasAttribute('removable');

    this.innerHTML = `<span data-ry-target="label" class="ry-tag__label">${text}</span>${
      removable
        ? `<button data-ry-target="remove" class="ry-tag__remove" aria-label="Remove" type="button">${getIcon('close')}</button>`
        : ''
    }`;

    if (removable) {
      const btn = this.$<HTMLButtonElement>('[data-ry-target="remove"]');
      if (btn) {
        this.on(btn, 'click', (e: MouseEvent) => {
          e.stopPropagation();
          const detail = { value: this.dataset.value ?? '', label: text };
          if (!this.emit('beforeremove', detail)) return;
          this.emit('remove', detail);
          this.remove();
        });
      }
    }
  }

  get label(): string {
    return this.$('[data-ry-target="label"]')?.textContent?.trim() ?? '';
  }
}

customElements.define('ry-tag', RyTag);
